import React, {useState} from 'react';
import {
  useForm,
  SubmitHandler,
  Controller,
  FormProvider
} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Dialog, {DialogProps} from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import {TextField} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {getCmsData} from '@shared/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {enqueueSnackbar} from 'notistack';

interface AddFolderProps extends DialogProps {
  token: string | undefined;
  parentId: string;
  setTreeData?: any;
  data?: any;
  // eslint-disable-next-line react/boolean-prop-naming
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
}

interface FormInputs {
  folderData: string;
}

const schema = yup.object().shape({
  folderData: yup
    .string()
    .required('Folder name is required')
    .min(3, 'Folder name must be at least 3 characters long')
});

const AddFolder: React.FC<AddFolderProps> = ({
  token,
  setTreeData,
  data,
  parentId,
  open,
  onClose,
  onCancel,
  ...other
}) => {
  const [name, setName] = useState('');
  const methods = useForm<FormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      folderData: name || ''
    }
  });
  const {
    control,
    handleSubmit,
    watch,
    formState: {errors, isSubmitting}
  } = methods;
  const handleSave = async (data: any) => {
    try {
      const uploadeddata = {
        Name: data.folderData,
        ParentId: parentId,
        NodeType: 'Folder',
        Icon: 'folder_icon',
        Access: true
      };
      const requestData: any = {
        sql_data: token,
        data: uploadeddata,
        operation: 'INSERT'
      };
      const encryptRequestData = EncryptUtils.encryptedData(requestData);
      const response = await getCmsData('LMS', 'content-management', {
        tk: encryptRequestData
      });
      const {Name, Id} = response?.data[0] || {};
      // Update tree data
      if (Name && Id) {
        setTreeData((prevTreeData: any[]) => {
          const updateTree = (nodes: any[]): any[] =>
            nodes.map(node => {
              if (node.Id === parentId) {
                return {
                  ...node,
                  children: [
                    ...(node.children || []),
                    {
                      Id: Id,
                      Name: Name,
                      ParentId: parentId,
                      NodeType: 'Folder',
                      Icon: 'folder_icon',
                      ChildrenFetched: true,
                      HasDocuments: false,
                      Access: true,
                      children: []
                    }
                  ]
                };
              }
              if (node.children) {
                return {
                  ...node,
                  children: updateTree(node.children)
                };
              }
              return node;
            });

          // If parentId is null or not found, add the new folder to the root level
          if (!parentId) {
            return [
              ...prevTreeData,
              {
                id: Id,
                name: Name,
                NodeType: 'Folder',
                Icon: 'folder_icon',
                Access: true,
                children: []
              }
            ];
          }

          return updateTree(prevTreeData);
        });
      }

      onClose();
      enqueueSnackbar('The Folder is Added', SnackBarConfig.getSuccess());
    } catch (e) {
      console.error('Error saving folder:', e);
      onClose();
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };
  window.addEventListener('keydown', event => {
    // Ignore key events if the target is an input, textarea, or contenteditable element
    const target = event.target as HTMLElement;

    // Check if the target is an input, textarea, or contenteditable element
    const isInputField =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable;

    if (isInputField) {
      return; // Exit if the focus is on an input field
    }

    // Handle other global key events here
    if (event.key === 'a' || event.key === 'd' || event.key === 'r') {
      console.log(`Key pressed: ${event.key}`);
      // Add your custom logic for these keys
    }
  });
  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose} {...other}>
      <DialogTitle>Add New Folder</DialogTitle>
      <DialogContent sx={{padding: '2vh !important'}}>
        <Controller
          name="folderData"
          defaultValue={name}
          control={control}
          render={({field}) => {
            return (
              <TextField
                {...field}
                type="text"
                label="Folder Name"
                fullWidth
                error={!!errors.folderData}
                helperText={errors.folderData?.message}
                onChange={e => {
                  // console.log('e.target.value', e);
                  // setName(e.target.value); // Your custom logic
                  field.onChange(e); // Call react-hook-form's onChange
                }}
                onKeyDown={event => {
                  event.stopPropagation(); // Prevent the event from bubbling to global handlers
                }}
              />
            );
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          color="success"
          onClick={handleSubmit(handleSave)} // Correctly invoke handleSubmit
          loading={isSubmitting}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddFolder;
